import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Progress, Badge, Spinner, Button } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { DollarSign, Activity, Users, Clock, TrendingUp, ArrowUpRight, ArrowDownRight, ArrowUpCircle, ArrowDownCircle, RefreshCw, BarChart2, AlertTriangle, CreditCard, MapPin, Briefcase, UserCheck, Star } from 'react-feather';
import { toast } from 'react-toastify';
import { apiGet, mediaUrl } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import { useConfig } from '../../../Context/ConfigContext';

interface LiveStats {
    stats: { total_revenue: { value: number; trend: number }; total_bookings: { value: number; trend: number }; active_services: { value: number }; pending_requests: { value: number }; };
    revenue_trend: { date: string; amount: number }[];
    top_bystanders: { name: string; profile_pic: string; bookings_count: number; revenue: number }[];
    recent_activity: { id: number; patient: string; bystander: string; status: string; time: string; amount: number }[];
    status_distribution: Record<string, number>;
    booking_type_split: Record<string, number>;
    location_split: Record<string, number>;
    payout_summary: { pending_amount: number; requested_amount: number; requested_count: number; completed_amount: number };
    patient_growth: { this_month: number; last_month: number; growth_rate: number; total_patients: number };
    cancellation_rate: number;
    avg_booking_value: number;
    bystander_status: { online: number; total: number };
}
interface FinancialSummary { direct_expenses: number; paid_payouts: number; pending_payouts: number; total_operational_cost: number; total_revenue: number; net_profit: number; }
interface MonthlyBreakdown { month: string; direct_expenses: number; bystander_payouts: number; total_expense: number; revenue: number; profit: number; patient_count?: number; booking_count?: number; }

const statusColor = (s: string) => ({ completed: 'success', active: 'primary', pending: 'warning', cancelled: 'danger', rejected: 'danger', expired: 'secondary', accepted: 'info' }[s.toLowerCase()] || 'light');

const KPI_CONFIGS = [
    { gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', shadow: 'rgba(99,102,241,0.25)', light: '#ede9fe', text: '#6366f1' },
    { gradient: 'linear-gradient(135deg,#0ea5e9,#06b6d4)', shadow: 'rgba(14,165,233,0.25)', light: '#e0f2fe', text: '#0ea5e9' },
    { gradient: 'linear-gradient(135deg,#10b981,#059669)', shadow: 'rgba(16,185,129,0.25)', light: '#d1fae5', text: '#10b981' },
    { gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', shadow: 'rgba(245,158,11,0.25)', light: '#fef3c7', text: '#f59e0b' },
];

export default function AnalyticsDashboard() {
    const { config } = useConfig();
    const cur = config?.currency || '₹';
    const [liveData, setLiveData] = useState<LiveStats | null>(null);
    const [finData, setFinData] = useState<FinancialSummary | null>(null);
    const [monthlyData, setMonthlyData] = useState<MonthlyBreakdown[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(document.body.classList.contains('dark-only'));

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.body.classList.contains('dark-only'));
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const colors = {
        bg: isDarkMode ? '#0f172a' : '#f1f5f9',
        card: isDarkMode ? '#1e293b' : '#ffffff',
        text: isDarkMode ? '#f1f5f9' : '#0f172a',
        muted: isDarkMode ? '#94a3b8' : '#64748b',
        border: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        grid: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
    };

    const fetchAll = useCallback(async (silent = false) => {
        silent ? setRefreshing(true) : setLoading(true);
        setError(false);
        try {
            const [live, fin] = await Promise.all([apiGet('admin/dashboard-stats'), apiGet('financial/summary')]);
            // console.log(fin.data.monthly_breakdown)
            if (live?.status && live?.data) setLiveData(live.data);
            if (fin?.status && fin?.data) { setFinData(fin.data.summary); setMonthlyData(fin.data.monthly_breakdown || []); }
        } catch { setError(true); toast.error('Could not fetch dashboard data'); }
        finally { setLoading(false); setRefreshing(false); }
    }, []);

    useEffect(() => { fetchAll(); const t = setInterval(() => fetchAll(true), 300000); return () => clearInterval(t); }, [fetchAll]);

    const trendOpts: any = {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, sparkline: { enabled: false }, background: 'transparent' },
        theme: { mode: isDarkMode ? 'dark' : 'light' },
        dataLabels: { enabled: false }, stroke: { curve: 'smooth', width: 2.5 },
        colors: [!isDarkMode ? '#fff' : '#6366f1'],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.05 } },
        xaxis: { categories: liveData?.revenue_trend.map(t => t.date) || [], labels: { style: { colors: !isDarkMode ? 'rgba(255,255,255,0.8)' : colors.muted, fontSize: '10px' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { show: false } }, grid: { borderColor: !isDarkMode ? 'rgba(255,255,255,0.15)' : colors.border, strokeDashArray: 4 },
        tooltip: { y: { formatter: (v: number) => `${cur} ${v.toLocaleString()}` } },
    };

    const chronologicalData = [...monthlyData].reverse();

    const areaTrendOpts: any = {
        chart: { height: 300, type: 'area', toolbar: { show: false }, zoom: { enabled: false }, dropShadow: { enabled: true, top: 10, left: 0, blur: 5, color: '#6366f1', opacity: 0.1 }, background: 'transparent' },
        theme: { mode: isDarkMode ? 'dark' : 'light' },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3, lineCap: 'round' },
        colors: ['#6366f1', '#f43f5e', '#10b981'],
        xaxis: { categories: chronologicalData.map(d => d.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: colors.muted, fontSize: '11px', fontWeight: 500 } } },
        yaxis: { labels: { style: { colors: colors.muted, fontSize: '11px', fontWeight: 500 }, formatter: (v: number) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v } },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.02, stops: [0, 90, 100] } },
        grid: { borderColor: colors.grid, strokeDashArray: 4, padding: { left: 10, right: 10 } },
        tooltip: { theme: isDarkMode ? 'dark' : 'light', x: { show: true }, y: { formatter: (v: number) => `${cur}${v.toLocaleString()}` } },
        legend: { position: 'top', horizontalAlign: 'right', labels: { colors: colors.muted, useSeriesColors: false }, markers: { radius: 12 } }
    };

    const perfOpts: any = {
        chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent' },
        theme: { mode: isDarkMode ? 'dark' : 'light' },
        dataLabels: { enabled: false }, stroke: { show: true, width: 2, colors: ['transparent'] },
        colors: ['#6366f1', '#10b981'],
        plotOptions: { bar: { horizontal: false, borderRadius: 6, columnWidth: '55%' } },
        xaxis: { categories: chronologicalData.map(d => d.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px', colors: colors.muted } } },
        yaxis: { labels: { show: false } }, grid: { borderColor: colors.grid, strokeDashArray: 4 },
        legend: { position: 'top', horizontalAlign: 'right', labels: { colors: colors.muted } },
        tooltip: { theme: isDarkMode ? 'dark' : 'light', y: { formatter: (v: number) => `${cur} ${v.toLocaleString()}` } },
        fill: { opacity: 1 },
    };

    const growthOpts: any = {
        chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent' },
        theme: { mode: isDarkMode ? 'dark' : 'light' },
        dataLabels: { enabled: false }, stroke: { curve: 'smooth', width: 3, dashArray: [0, 8] },
        colors: ['#f59e0b', '#6366f1'],
        xaxis: { categories: chronologicalData.map(d => d.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: colors.muted, fontSize: '11px' } } },
        yaxis: { labels: { style: { colors: colors.muted, fontSize: '11px' } } },
        grid: { borderColor: colors.grid, strokeDashArray: 4 },
        legend: { position: 'top', horizontalAlign: 'right', labels: { colors: colors.muted } },
        markers: { size: 4, strokeWidth: 2, hover: { size: 6 } },
        tooltip: { theme: isDarkMode ? 'dark' : 'light' }
    };

    if (loading) return (
        <div style={{ background: colors.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="text-center">
                <Spinner style={{ width: '3rem', height: '3rem', color: '#6366f1' }} />
                <p className="mt-3 fw-semibold" style={{ color: colors.muted }}>Loading analytics…</p>
            </div>
        </div>
    );

    if (error || !liveData || !finData) return (
        <div style={{ background: colors.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="text-center">
                <div style={{ fontSize: '4rem' }}>📊</div>
                <h4 className="mt-3" style={{ color: '#ef4444' }}>Unable to load dashboard</h4>
                <p className="mb-4" style={{ color: colors.muted }}>Check your server connection and try again.</p>
                <Button onClick={() => fetchAll()} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: '12px', padding: '10px 28px', color: 'white' }}>Retry</Button>
            </div>
        </div>
    );

    const profitMargin = finData.total_revenue > 0 ? ((finData.net_profit / finData.total_revenue) * 100).toFixed(1) : '0';

    const liveKpis = [
        { label: "Today's Revenue", val: `${cur}${liveData.stats.total_revenue.value.toLocaleString()}`, icon: <DollarSign size={22} />, trend: liveData.stats.total_revenue.trend },
        { label: 'Bookings Today', val: String(liveData.stats.total_bookings.value), icon: <Activity size={22} />, trend: liveData.stats.total_bookings.trend },
        { label: 'Active Services', val: String(liveData.stats.active_services.value), icon: <Users size={22} />, badge: 'Live' },
        { label: 'Pending Requests', val: String(liveData.stats.pending_requests.value), icon: <Clock size={22} />, badge: 'Waiting' },
    ];

    const finKpis = [
        { label: 'Total Revenue', val: `${cur}${finData.total_revenue.toLocaleString()}`, icon: <TrendingUp size={20} />, idx: 0 },
        { label: 'Operational Cost', val: `${cur}${finData.total_operational_cost.toLocaleString()}`, icon: <ArrowDownCircle size={20} />, idx: 1 },
        { label: `Net Profit · ${profitMargin}%`, val: `${cur}${finData.net_profit.toLocaleString()}`, icon: <DollarSign size={20} />, idx: 2 },
        { label: 'Pending Payouts', val: `${cur}${finData.pending_payouts.toLocaleString()}`, icon: <Users size={20} />, idx: 3 },
    ];

    const card: React.CSSProperties = { background: colors.card, borderRadius: '20px', boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.4)' : '0 1px 24px rgba(100,116,139,0.08)', border: `1px solid ${colors.border}` };
    const sectionTitle = (icon: React.ReactNode, color: string, label: string) => (
        <div className="d-flex align-items-center gap-2 mb-3">
            <div style={{ color }}>{icon}</div>
            <span style={{ color, fontWeight: 700, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{label}</span>
        </div>
    );

    return (
        <div style={{ background: colors.bg, minHeight: '100vh', transition: 'all 0.3s ease' }}>
            <style>{`
                .page-wrapper.compact-wrapper .page-body-wrapper .page-body {
                    background-color: ${colors.bg} !important;
                }
                .card {
                    background-color: ${colors.card} !important;
                    border-color: ${colors.border} !important;
                }
                .table {
                    --bs-table-bg: ${colors.card} !important;
                    --bs-table-color: ${colors.text} !important;
                    background-color: ${colors.card} !important;
                    color: ${colors.text} !important;
                }
                .table th, .table td {
                    border-color: ${colors.border} !important;
                    color: ${colors.text} !important;
                }
                .breadcrumb-item a, .breadcrumb-item.active {
                    color: ${colors.muted} !important;
                }
                .page-title h4 {
                    color: ${colors.text} !important;
                }
                .text-muted {
                    color: ${colors.muted} !important;
                }
                .premium-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                }
                .premium-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
                }
                .gradient-card-primary {
                    background: linear-gradient(135deg, var(--theme-default), #4eb7b0) !important;
                }
                .kpi-icon-bg {
                    transition: transform 0.3s ease;
                }
                .premium-card:hover .kpi-icon-bg {
                    transform: scale(1.1) rotate(5deg);
                }
                    .fin-card:hover {
  transform: translateY(-10px) scale(1.03);
}

/* 🔥 spinning border */
.fin-spin {
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  opacity: 0;
  z-index: 0;
  filter: blur(20px);
  animation: spin 4s linear infinite;
}
.fin-card:hover .fin-spin {
  opacity: 0.4;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 🔥 glow */
.fin-glow {
  position: absolute;
  inset: -2px;
  border-radius: 20px;
  background: linear-gradient(120deg, #6366f1, #8b5cf6, #0ea5e9);
  opacity: 0;
  transition: 0.4s;
  filter: blur(25px);
}
.fin-card:hover .fin-glow {
  opacity: 0.25;
}

/* 🔥 blob */
.fin-blob {
  position: absolute;
  width: 130px;
  height: 130px;
  top: -40px;
  right: -40px;
  opacity: 0.18;
  border-radius: 50%;
  filter: blur(30px);
  animation: floatBlob 6s ease-in-out infinite;
}
@keyframes floatBlob {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(14px); }
}

/* 🔥 shimmer */
.fin-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    110deg,
    transparent,
    rgba(255,255,255,0.2),
    transparent
  );
  animation: shimmer 4s infinite;
}
@keyframes shimmer {
  0% { left: -100%; }
  60% { left: 120%; }
  100% { left: 120%; }
}

/* icon */
.fin-icon {
  border-radius: 12px;
  padding: 10px;
  display: inline-flex;
  margin-bottom: 16px;
  color: #fff;
  position: relative;
  z-index: 2;
}

/* value */
.fin-value {
  font-size: 1.65rem;
  font-weight: 900;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 2;
}

/* label */
.fin-label {
  font-size: 12px;
  margin-top: 6px;
  font-weight: 600;
  position: relative;
  z-index: 2;
}

/* bottom line */
.fin-line {
  position: absolute;
  bottom: 0;
  left: 12px;
  right: 12px;
  height: 3px;
  border-radius: 999px;
  opacity: 0.8;
  animation: lineGlow 3s infinite;
}
@keyframes lineGlow {
  0%,100% { opacity: 0.6; }
  50% { opacity: 1; box-shadow: 0 0 10px currentColor; }
}

/* badge */
.profit-badge {
  position: absolute;
  top: 14px;
  right: 14px;
  background: linear-gradient(135deg, #10b981, #22c55e);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(16,185,129,0.4);
}
            `}</style>
            <Breadcrumbs pageTitle="Dashboard" parent="Dashboard" title="Overview" />

            <div className="px-4 pb-5">


                {/* Financial KPI Cards */}
                {sectionTitle(<TrendingUp size={14} />, '#10b981', 'Financial Summary')}
                {/* <Row className="g-3 mb-4">
                    {finKpis.map((k) => (
                        <Col xl="3" md="6" key={k.idx}>
                            <div className="premium-card" style={{ ...card, padding: '22px', position: 'relative', overflow: 'hidden' }}>
                                <div className="kpi-icon-bg" style={{ background: KPI_CONFIGS[k.idx].light, borderRadius: '12px', padding: '10px', display: 'inline-flex', marginBottom: '16px', opacity: isDarkMode ? 0.9 : 1 }}>
                                    {React.cloneElement(k.icon as any, { color: KPI_CONFIGS[k.idx].text, size: 20 })}
                                </div>
                                <div style={{ fontSize: '1.45rem', fontWeight: 800, color: colors.text, lineHeight: 1.1 }}>{k.val}</div>
                                <div style={{ color: colors.muted, fontSize: '12px', marginTop: 5, fontWeight: 600 }}>{k.label}</div>
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: KPI_CONFIGS[k.idx].gradient, borderRadius: '0 0 20px 20px' }} />
                     
                                <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '80px', height: '80px', background: KPI_CONFIGS[k.idx].gradient, opacity: 0.05, borderRadius: '50%', pointerEvents: 'none' }} />
                            </div>
                        </Col>
                    ))}
                </Row> */}
                <Row className="g-4 mb-4">
                    {finKpis.map((k) => (
                        <Col xl="3" md="6" key={k.idx}>
                            <div
                                className="fin-card"
                                style={{
                                    position: "relative",
                                    borderRadius: "20px",
                                    padding: "22px",
                                    overflow: "hidden",
                                    backdropFilter: "blur(16px)",
                                    WebkitBackdropFilter: "blur(16px)",
                                    background: isDarkMode
                                        ? "rgba(255,255,255,0.05)"
                                        : "rgba(255,255,255,0.75)",
                                    border: `1px solid ${colors.border}`,
                                    boxShadow: isDarkMode
                                        ? "0 10px 35px rgba(0,0,0,0.6)"
                                        : "0 10px 30px rgba(0,0,0,0.08)",
                                    transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                                }}
                            >
                                {/* 🔥 Spinning gradient border */}
                                <div
                                    className="fin-spin"
                                    style={{ background: KPI_CONFIGS[k.idx].gradient }}
                                />

                                {/* 🔥 Glow */}
                                <div className="fin-glow" />

                                {/* 🔥 Floating Blob */}
                                <div
                                    className="fin-blob"
                                    style={{ background: KPI_CONFIGS[k.idx].gradient }}
                                />

                                {/* 🔥 Shimmer sweep */}
                                <div className="fin-shimmer" />

                                {/* ICON */}
                                <div
                                    className="fin-icon"
                                    style={{
                                        background: KPI_CONFIGS[k.idx].gradient,
                                        boxShadow: KPI_CONFIGS[k.idx].shadow,
                                    }}
                                >
                                    {k.icon}
                                </div>

                                {/* VALUE */}
                                <div className="fin-value" style={{ color: colors.text }}>
                                    {k.val}
                                </div>

                                {/* LABEL */}
                                <div className="fin-label" style={{ color: colors.muted }}>
                                    {k.label}
                                </div>

                                {/* Bottom accent line */}
                                <div
                                    className="fin-line"
                                    style={{ background: KPI_CONFIGS[k.idx].gradient }}
                                />
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Row 4: 7-Day Revenue + Revenue vs Profit (Bar) + Top Bystanders */}
                {sectionTitle(<Activity size={14} />, 'var(--theme-default)', 'Performance Snapshots')}
                <Row className="g-3 mb-4" >
                    <Col xl="4" md="12" >
                        <div className="premium-card" style={{
                            ...card,
                            padding: '24px',
                            height: '100%',
                            background: !isDarkMode ? 'linear-gradient(135deg, var(--theme-default) 0%, #4eb7b0 100%)' : colors.card,
                            border: !isDarkMode ? 'none' : card.border,
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Decorative glass circles for light mode */}
                            {!isDarkMode && (
                                <>
                                    <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                                    <div style={{ position: 'absolute', bottom: '-30px', left: '-10px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                                </>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span style={{ color: !isDarkMode ? '#fff' : colors.text, fontWeight: 700, fontSize: '15px' }}>7-Day Revenue</span>
                                <span style={{ background: !isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.1)', color: !isDarkMode ? '#fff' : '#6366f1', borderRadius: '8px', padding: '3px 10px', fontSize: '11px', fontWeight: 600 }}>Daily</span>
                            </div>
                            <ReactApexChart options={trendOpts}
                                series={[{ name: 'Revenue', data: liveData.revenue_trend.map(t => t.amount) }]}
                                type="area" height={280} />
                        </div>
                    </Col>
                    <Col xl="5" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span style={{ color: colors.text, fontWeight: 700, fontSize: '15px' }}>Revenue vs Profit</span>
                                <span style={{ color: colors.muted, fontSize: '12px' }}>6 Months</span>
                            </div>
                            <ReactApexChart options={perfOpts}
                                series={[{ name: 'Revenue', data: chronologicalData.map(d => d.revenue) }, { name: 'Profit', data: chronologicalData.map(d => d.profit) }]}
                                type="bar" height={280} />
                        </div>
                    </Col>
                    <Col xl="3" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span style={{ color: colors.text, fontWeight: 700, fontSize: '15px' }}>Top Bystanders</span>
                                <span style={{ background: isDarkMode ? 'rgba(99,102,241,0.2)' : '#ede9fe', color: '#6366f1', borderRadius: '8px', padding: '3px 10px', fontSize: '11px', fontWeight: 600 }}>By Revenue</span>
                            </div>
                            {liveData.top_bystanders.length === 0
                                ? <p style={{ color: colors.muted, textAlign: 'center', marginTop: '32px' }}>No data yet</p>
                                : liveData.top_bystanders.slice(0, 5).map((b, i) => (
                                    <div key={i} className="d-flex align-items-center gap-3 py-2" style={{ borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none' }}>
                                        <div style={{ width: 32, height: 32, minWidth: 32, borderRadius: '50%', background: KPI_CONFIGS[i % 4].gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 12, overflow: 'hidden' }}>
                                            {b.profile_pic ? (
                                                <img src={`${mediaUrl}${b.profile_pic}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                                            ) : null}
                                            <span style={{ display: b.profile_pic ? 'none' : 'block' }}>{b.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div style={{ color: colors.text, fontWeight: 600, fontSize: '12px' }}>{b.name}</div>
                                            <div style={{ color: colors.muted, fontSize: '10px' }}>{b.bookings_count} bookings</div>
                                        </div>
                                        <span style={{ color: '#6366f1', fontWeight: 700, fontSize: '12px' }}>{cur}{Number(b.revenue).toLocaleString()}</span>
                                    </div>
                                ))}
                        </div>
                    </Col>
                </Row>

                {/* Row 2: Financial Performance Trend & Booking Status */}

                {sectionTitle(<TrendingUp size={14} />, '#6366f1', 'Financial & Operational Trends')}

                <Row className="g-3 mb-4">
                    <Col xl="8" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#6366f1' }} />
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h6 style={{ color: colors.text, fontWeight: 800, marginBottom: 2, fontSize: '16px' }}>Revenue & Profit Velocity</h6>
                                    <p style={{ color: colors.muted, fontSize: '12px', marginBottom: 0 }}>Comparison of monthly gross revenue against net margins</p>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{ background: isDarkMode ? 'rgba(99,102,241,0.2)' : '#eef2ff', color: '#6366f1', borderRadius: '20px', padding: '4px 14px', fontSize: '11px', fontWeight: 700 }}>
                                        {monthlyData[0]?.month} Peak
                                    </div>
                                </div>
                            </div>
                            <ReactApexChart options={areaTrendOpts}
                                series={[
                                    { name: 'Total Revenue', data: chronologicalData.map(d => d.revenue) },
                                    { name: 'Operational Cost', data: chronologicalData.map(d => d.total_expense) },
                                    { name: 'Net Profit', data: chronologicalData.map(d => d.profit) }
                                ]}
                                type="area" height={300} />
                        </div>
                    </Col>
                    <Col xl="4" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#0ea5e9' }} />
                            <h6 style={{ color: colors.text, fontWeight: 800, marginBottom: 4, fontSize: '16px' }}>Booking Vitality</h6>
                            <p style={{ color: colors.muted, fontSize: '12px', marginBottom: 25 }}>Service fulfillment distribution</p>

                            <div style={{ position: 'relative' }}>
                                <ReactApexChart
                                    options={{
                                        chart: { type: 'donut', dropShadow: { enabled: true, blur: 4, color: '#000', opacity: 0.05 }, background: 'transparent' },
                                        labels: Object.keys(liveData.status_distribution).map(s => s.charAt(0).toUpperCase() + s.slice(1)),
                                        colors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#94a3b8', '#0ea5e9', '#8b5cf6'],
                                        legend: { show: true, position: 'bottom', fontSize: '11px', fontWeight: 600, labels: { colors: colors.text }, itemMargin: { vertical: 5 } },
                                        plotOptions: {
                                            pie: {
                                                donut: {
                                                    size: '82%',
                                                    labels: {
                                                        show: true,
                                                        total: {
                                                            show: true,
                                                            label: 'Total Bookings',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            color: colors.muted,
                                                            formatter: () => Object.values(liveData.status_distribution).reduce((a, b) => a + b, 0).toLocaleString()
                                                        },
                                                        value: { fontSize: '20px', fontWeight: 800, color: colors.text }
                                                    }
                                                }
                                            }
                                        },
                                        stroke: { width: 0 },
                                        dataLabels: { enabled: false },
                                        tooltip: { theme: isDarkMode ? 'dark' : 'light' }
                                    }}
                                    series={Object.values(liveData.status_distribution)}
                                    type="donut" height={320} />
                            </div>
                        </div>
                    </Col>
                </Row>



                {/* Row 3: Booking Type Split + Service Location + Cost Breakdown */}
                {sectionTitle(<BarChart2 size={14} />, '#8b5cf6', 'Breakdowns & Costs')}
                <Row className="g-3 mb-4">
                    <Col xl="3" md="6">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <h6 style={{ color: colors.text, fontWeight: 700, marginBottom: 15, fontSize: '14px' }}>Booking Type Split</h6>
                            <ReactApexChart
                                options={{
                                    chart: { type: 'pie', background: 'transparent' },
                                    labels: Object.keys(liveData.booking_type_split).map(t => t === 'booknow' ? 'Book Now' : 'Plan Ahead'),
                                    colors: ['#8b5cf6', '#ec4899'],
                                    legend: { position: 'bottom', labels: { colors: colors.text } },
                                    dataLabels: { dropShadow: { enabled: false } }
                                }}
                                series={Object.values(liveData.booking_type_split)}
                                type="pie" height={220} />
                        </div>
                    </Col>
                    <Col xl="3" md="6">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <h6 style={{ color: colors.text, fontWeight: 700, marginBottom: 15, fontSize: '14px' }}>Service Location</h6>
                            <ReactApexChart
                                options={{
                                    chart: { type: 'pie', background: 'transparent' },
                                    labels: Object.keys(liveData.location_split).map(l => l.charAt(0).toUpperCase() + l.slice(1)),
                                    colors: ['#0ea5e9', '#10b981'],
                                    legend: { position: 'bottom', labels: { colors: colors.text } },
                                    dataLabels: { dropShadow: { enabled: false } }
                                }}
                                series={Object.values(liveData.location_split)}
                                type="pie" height={220} />
                        </div>
                    </Col>
                    <Col xl="6" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <span style={{ color: colors.text, fontWeight: 700, fontSize: '15px' }}>Cost Breakdown</span>
                            <div className="mt-4">
                                <div className="d-flex justify-content-between mb-1">
                                    <span style={{ color: colors.muted, fontSize: '12px', fontWeight: 600 }}>Direct Expenses</span>
                                    <span style={{ color: colors.text, fontSize: '12px', fontWeight: 700 }}>{cur}{finData.direct_expenses.toLocaleString()}</span>
                                </div>
                                <div style={{ background: isDarkMode ? '#1e293b' : '#f1f5f9', borderRadius: '6px', height: '8px', marginBottom: '20px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', borderRadius: '6px', background: 'linear-gradient(90deg,#f59e0b,#fbbf24)', width: `${finData.total_operational_cost > 0 ? (finData.direct_expenses / finData.total_operational_cost) * 100 : 0}%` }} />
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span style={{ color: colors.muted, fontSize: '12px', fontWeight: 600 }}>Bystander Payouts</span>
                                    <span style={{ color: colors.text, fontSize: '12px', fontWeight: 700 }}>{cur}{finData.paid_payouts.toLocaleString()}</span>
                                </div>
                                <div style={{ background: isDarkMode ? '#1e293b' : '#f1f5f9', borderRadius: '6px', height: '8px', marginBottom: '24px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', borderRadius: '6px', background: 'linear-gradient(90deg,#0ea5e9,#06b6d4)', width: `${finData.total_operational_cost > 0 ? (finData.paid_payouts / finData.total_operational_cost) * 100 : 0}%` }} />
                                </div>
                                <div style={{ background: isDarkMode ? 'rgba(99,102,241,0.1)' : 'linear-gradient(135deg,#ede9fe,#dbeafe)', borderRadius: '14px', padding: '16px' }}>
                                    <div style={{ color: '#6366f1', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', marginBottom: 4, textTransform: 'uppercase' }}>Net Profit Margin</div>
                                    <div style={{ color: finData.net_profit >= 0 ? '#10b981' : '#ef4444', fontSize: '1.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {finData.net_profit >= 0 ? <ArrowUpCircle size={20} /> : <ArrowDownCircle size={20} />}
                                        {profitMargin}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>


                {/* Row 4: Customer & Booking Growth & Monthly Financial Breakdown */}
                <Row className="g-3 mb-4">
                    <Col xl="7" md="12">
                        <div style={{ ...card, padding: '24px', height: '100%' }}>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h6 style={{ color: colors.text, fontWeight: 700, marginBottom: 2 }}>Customer & Booking Growth</h6>
                                    <p style={{ color: colors.muted, fontSize: '12px', marginBottom: 0 }}>Monthly expansion of patient base and transaction volume</p>
                                </div>
                            </div>
                            <ReactApexChart options={growthOpts}
                                series={[
                                    { name: 'Bookings', data: chronologicalData.map(d => d.booking_count || 0) },
                                    { name: 'Patients', data: chronologicalData.map(d => d.patient_count || 0) }
                                ]}
                                type="line" height={320} />
                        </div>
                    </Col>
                    <Col xl="5" md="12">
                        <div style={{ ...card, overflow: 'hidden', height: '100%' }}>
                            <div style={{ padding: '20px 24px 14px', borderBottom: `1px solid ${colors.border}`, background: 'rgba(0,0,0,0.05)' }}>
                                <span style={{ color: colors.text, fontWeight: 700, fontSize: '15px' }}>Monthly Financial Breakdown</span>
                            </div>
                            <div className="table-responsive">
                                <table className="table mb-0 align-middle" style={{ fontSize: '12px', background: colors.card }}>
                                    <thead>
                                        <tr style={{ background: 'rgba(0,0,0,0.1)', borderBottom: `2px solid ${colors.border}` }}>
                                            {['Month', 'Revenue', 'Total Exp.', 'Net Profit'].map(h => (
                                                <th key={h} className="px-3 py-3 border-0 fw-bold" style={{ color: colors.muted, fontSize: '10px', letterSpacing: '0.8px', textTransform: 'uppercase', background: 'transparent' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {monthlyData.map((row, i) => (
                                            <tr key={i} style={{ borderBottom: `1px solid ${colors.border}`, background: 'transparent' }}>
                                                <td className="px-3 py-2 fw-bold" style={{ color: colors.text, background: 'transparent' }}>{row.month}</td>
                                                <td className="py-2 fw-bold" style={{ color: '#6366f1', background: 'transparent' }}>{cur}{row.revenue.toLocaleString()}</td>
                                                <td className="py-2" style={{ color: colors.muted, background: 'transparent' }}>{cur}{row.total_expense.toLocaleString()}</td>
                                                <td className="py-2">
                                                    <span style={{ background: row.profit >= 0 ? '#dcfce7' : '#fee2e2', color: row.profit >= 0 ? '#16a34a' : '#dc2626', borderRadius: '20px', padding: '2px 8px', fontWeight: 700, fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                        {row.profit >= 0 ? <ArrowUpCircle size={10} /> : <ArrowDownCircle size={10} />}
                                                        {cur}{Math.abs(row.profit).toLocaleString()}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Col>
                </Row>

            </div>

            <style>{`
                .spin-icon { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .page-body { background: ${colors.bg} !important; }
            `}</style>
        </div>
    );
}
